import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useGeoMarkupStore } from '@/store';
import { useWorkspace } from '@/hooks/useWorkspace';
import { useNotifications } from '@/hooks/useNotifications';
import {
  MAP_SETTINGS_VERSION,
  MAP_SETUP_DEFAULT_PL_COUNTRY_CODE,
} from '@/constants';
import {
  SETTINGS_SECTIONS,
  type SettingsLocationState,
} from '@/pages/SettingsPage/constants';
import type { MapSettings } from '@/types';
import { useMapSetupForm, type UseMapSetupForm } from './useMapSetupForm';
import type { MapSetupMode } from '../types';

/** Navigation state that lands the user back on the Settings → Workspace section. */
const SETTINGS_WORKSPACE_STATE: SettingsLocationState = {
  section: SETTINGS_SECTIONS.workspace,
};

/** Everything the configurator page needs, bundled to keep the page thin. */
export interface UseMapConfigPage {
  /** `edit` when reached from settings (`?mode=edit`), else `firstRun`. */
  mode: MapSetupMode;
  /** Whether the page is in reversible edit mode. */
  isEdit: boolean;
  /** The configurator form API. */
  form: UseMapSetupForm;
  /** Whether a valid configuration can be saved (a center + ≥1 allowed basemap). */
  canSave: boolean;
  /** Whether a save request is in flight. */
  isSaving: boolean;
  /** Persist the configuration, then redirect (dashboard / settings). */
  handleSave: () => void;
  /** Return to settings without saving (edit mode only). */
  handleBack: () => void;
  /**
   * Dashboard path to bounce to when an already-configured workspace lands on
   * the first-run configurator (else `null`). The page renders `<Navigate>` so
   * the heavy live preview never mounts in the throwaway path.
   */
  redirectTo: string | null;
}

/**
 * Orchestration hook for the map configurator page: resolves the workspace from
 * the route, syncs it active, owns the form, and persists on save (mirroring the
 * old panel — `updateWorkspace` then `setWorkspaceMapConfigured`).
 */
export function useMapConfigPage(): UseMapConfigPage {
  const { t } = useTranslation();
  const { shortId } = useParams<{ shortId: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { showError } = useNotifications();

  const {
    workspaces,
    currentWorkspaceId,
    updateWorkspace,
    setCurrentWorkspace,
  } = useWorkspace();
  const setWorkspaceMapConfigured = useGeoMarkupStore(
    (state) => state.auth.setWorkspaceMapConfigured
  );

  const isEdit = searchParams.get('mode') === 'edit';
  const mode: MapSetupMode = isEdit ? 'edit' : 'firstRun';

  const activeWorkspace = workspaces.find(
    (workspace) => workspace.shortId === shortId
  );
  const workspaceId = activeWorkspace?.workspaceId ?? null;

  // Drive the form off the route-resolved workspace, not currentWorkspaceId, so
  // it loads the target workspace's settings even before the active-workspace
  // sync below runs.
  const form = useMapSetupForm(workspaceId);
  const [isSaving, setIsSaving] = useState(false);

  // Keep the resolved workspace active so the form reads its saved settings.
  useEffect(() => {
    if (workspaceId && workspaceId !== currentWorkspaceId) {
      setCurrentWorkspace(workspaceId);
    }
  }, [workspaceId, currentWorkspaceId, setCurrentWorkspace]);

  // Self-guard: a configured workspace has no first-run setup. If we land here
  // for one (e.g. direct URL, or a workspace switch that preserved the route),
  // bounce to its dashboard — the inverse of the AppShell first-run gate
  // (unconfigured → map-config), which can't fire here as this page is mounted
  // outside the AppShell. Edit mode (?mode=edit) is intentionally exempt.
  const redirectTo =
    !isEdit && activeWorkspace?.isMapConfigured && shortId
      ? `/w/${shortId}/dashboard`
      : null;

  const { values } = form;
  // A center is required; at least one allowed basemap is required too (an empty
  // allowlist would leave the shared/configured map with no basemap to render).
  const canSave = values.center !== null && values.allowedBasemapIds.length > 0;

  const handleSave = useCallback((): void => {
    if (!workspaceId || !values.center) return;

    const mapSettings: MapSettings = {
      version: MAP_SETTINGS_VERSION,
      center: { lat: values.center.lat, lng: values.center.lng },
      startZoom: values.startZoom,
      minZoom: values.minZoom,
      maxZoom: values.maxZoom,
      countryCode: values.countryCode ?? MAP_SETUP_DEFAULT_PL_COUNTRY_CODE,
      defaultBasemapId: values.defaultBasemapId,
      allowedBasemapIds: values.allowedBasemapIds,
    };

    setIsSaving(true);
    void (async () => {
      try {
        const result = await updateWorkspace(workspaceId, { mapSettings });
        if (result.success) {
          setWorkspaceMapConfigured(workspaceId, true);
          if (isEdit) {
            // Land back on the Workspace section the user opened the editor from.
            navigate(`/w/${shortId}/settings`, {
              state: SETTINGS_WORKSPACE_STATE,
            });
          } else {
            navigate(`/w/${shortId}/dashboard`);
          }
        } else {
          showError(t('mapSetup.saveError'));
        }
      } catch (error) {
        // Never block the user: surface the failure and let them retry.
        console.error('Failed to save map configuration:', error);
        showError(t('mapSetup.saveError'));
      } finally {
        setIsSaving(false);
      }
    })();
  }, [
    workspaceId,
    values,
    isEdit,
    shortId,
    updateWorkspace,
    setWorkspaceMapConfigured,
    navigate,
    showError,
    t,
  ]);

  const handleBack = useCallback((): void => {
    navigate(`/w/${shortId}/settings`, { state: SETTINGS_WORKSPACE_STATE });
  }, [navigate, shortId]);

  return {
    mode,
    isEdit,
    form,
    canSave,
    isSaving,
    handleSave,
    handleBack,
    redirectTo,
  };
}
